/**
 * Cliente HTTP del resumen post-sesión para `apps/speak`.
 *
 * El backend (`apps/backend/src/routes/english/sessions.ts:158`) devuelve un
 * envelope `{ success, data: { ready, summary } }`:
 *   - `ready=false, summary=null` mientras el job de distill todavía no
 *     terminó (corre async via pg-boss después del cierre de la sesión).
 *   - `ready=true, summary={...}` cuando ya hay datos.
 *
 * Los tipos están redefinidos acá en lugar de importar de `apps/client`
 * porque speak debe quedar autocontenida (ver §"connect antes que
 * reimplementar" en Fase 07 — la decisión es no acoplar speak al cliente
 * viejo). Si en el futuro hace falta compartir el shape, promoverlo a
 * `packages/shared-types` con zod schema.
 */

import { createTypedApiClient } from '@orborix/auth-client';

import { browserTimeZone } from './englishApi.js';
import type {
  CanDoCapstoneStatus,
  CanDoNextStepView,
  CanDoUnitStatus,
  LocalizedText,
} from './canDoApi.js';

const BASE = '/api/modules/english';

export type CefrLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
/**
 * Dimensiones REALES del currículum (espejo de `Dimension` en
 * `@orborix/english-curriculum`). Hasta 2026-09-09 este alias declaraba las
 * cuatro macro-habilidades (`listening | speaking | reading | writing |
 * interaction`), que el backend no manda desde que las dimensiones pasaron a
 * ser las del currículum — el único consumidor no lee el campo, así que el
 * drift no rompía nada, pero mentía sobre el contrato.
 */
export type EnglishDimension = 'grammar' | 'vocabulary' | 'functional';

export type SessionSummaryHeadlineTone = 'celebrate' | 'encourage' | 'short';
// Espejo de `CorrectionSeverity` del backend (`@orborix/module-languages`). El
// summary incluye `suggestion` (NO es error: "más natural", se pinta celeste);
// el debrief la colorea vía `gramSeverityStyle`. Antes faltaba `suggestion` y el
// destill mostraba todo rojo — ver `screens/conversation/severityStyle.ts`.
export type CorrectionSeverity = 'minor' | 'structural' | 'communication-breaking' | 'suggestion';

export interface SessionSummaryNewVocabItem {
  word: string;
  translation?: string;
  partOfSpeech?: string;
}

export interface SessionSummaryError {
  pattern: string;
  example?: string;
  category?: string;
}

export interface SessionSummaryCorrection {
  id: string;
  userUtterance: string;
  correctedForm: string;
  explanation?: string;
  category?: string;
  severity: CorrectionSeverity;
  turnIndex?: number;
  /** Voto previo del usuario (plan 26 §7.4) — rehidrata los thumbs. */
  feedback?: { vote: 'up' | 'down'; reason?: string };
  /**
   * P-A: cuántas correcciones crudas del MISMO patrón representa este chip
   * canónico (agrupado de presentación del backend). Ausente = 1.
   */
  repeatCount?: number;
  /**
   * El backend tiene grabado el turno donde el user dijo esto → el debrief
   * ofrece escucharlo (`DebriefTurnAudioBtn`). Ausente = sin grabación (o
   * grabación apagada): NO se pinta el botón, en vez de un play que da 404.
   */
  hasUserAudio?: boolean;
}

/**
 * Corrección fonética persistida (espejo de `SessionSummaryPhoneticCorrection`
 * del backend). El debrief la muestra en una sección aparte; la conversación
 * viva ya renderiza la fonética desde el WS.
 */
export interface SessionSummaryPhoneticCorrection {
  id: string;
  word: string;
  saidIpa?: string;
  targetIpa?: string;
  tip?: string;
  turnIndex?: number;
}

export interface SessionSummaryHeadline {
  tone: SessionSummaryHeadlineTone;
  title: string;
  subtitle: string;
  /**
   * Presente cuando el headline sale del conteo de correcciones: las piezas
   * para recomponerlo en vivo con las filas no descartadas (`liveHeadline`).
   * La elección de qué headline aplica la hizo el backend.
   */
  byCorrections?: {
    threshold: number;
    /** Título con `{n}`. */
    template: string;
    subtitle: string;
    fallback: { title: string; subtitle: string };
  };
}

/** Palabra del catálogo (Biblioteca) usada en la sesión (espejo del backend). */
export interface SessionSummaryCoverageWord {
  text: string;
  translation: string | null;
  band: number | null;
  /** Primera vez que entra a la cobertura del user. */
  isNew: boolean;
}

/**
 * Delta de cobertura de Biblioteca (fase 20.G): palabras que el user usó por su
 * cuenta y que cuentan para el medidor de cobertura. Distinto de `newVocab`
 * (vocab que el distiller marcó como aprendido). Ausente cuando no se usó
 * ninguna palabra del catálogo.
 */
export interface SessionSummaryCoverageDelta {
  used: number;
  newToCoverage: number;
  /** `true` mientras el distill no escribió → `newToCoverage` no es definitivo. */
  pending: boolean;
  words: ReadonlyArray<SessionSummaryCoverageWord>;
}

/** Tema legible de una corrección (lista cerrada del backend, `correction-topics.ts`). */
export type CorrectionTopicId =
  | 'articles'
  | 'prepositions'
  | 'verbs'
  | 'questions'
  | 'agreement'
  | 'subject'
  | 'word-order'
  | 'connectors'
  | 'adjectives'
  | 'vocabulary'
  | 'naturalness'
  | 'other';

/** Fila de "Lo que trabajamos hoy" — espejo de `SessionSummaryDistinctCorrection`. */
export interface SessionSummaryDistinctCorrection extends SessionSummaryCorrection {
  /** Ids de TODAS las filas crudas que representa — las acciones aplican a todas. */
  memberIds: ReadonlyArray<string>;
  /** "Ya lo sé": visible pero fuera del repaso. */
  known: boolean;
  topic: { id: CorrectionTopicId; label: string };
  /**
   * Charla asistida, opción A: cómo le fue en el repaso con el coach y cuándo
   * vuelve. Ausente = nunca se repasó.
   */
  review?: SessionSummaryCorrectionReview;
  /**
   * La frase que se repasa (la oración corregida, o su pedazo; la pone el server).
   * El repaso del cierre junta en una tarjeta las que terminan en la misma frase,
   * como el Repaso del día (card #580). Ausente en respuestas viejas.
   */
  reviewPhrase?: string;
  /** Ningún pedazo de su oración se dice solo: no entra al repaso (card #580). */
  reviewSkip?: true;
}

export interface SessionSummaryCorrectionReview {
  /** `memoria` = la dijo sin mirar; `practicada` = no salió sin mirar. */
  result: 'memoria' | 'practicada';
  at: string;
  nextReviewAt: string | null;
  /** 3 vueltas de memoria: aprendida, no vuelve más. */
  learned: boolean;
  /** Salió al 2.º o 3.º intento: bien, pero no «de memoria» (card #580). */
  retry?: true;
}

export interface SessionSummaryCommon {
  newVocab: ReadonlyArray<SessionSummaryNewVocabItem>;
  recurrentErrors: ReadonlyArray<SessionSummaryError>;
  /** Agrupadas por patrón (P-A) — alimentan el deck de práctica. */
  sessionCorrections: ReadonlyArray<SessionSummaryCorrection>;
  /**
   * Una fila por corrección DISTINTA (sólo la misma corrección repetida se
   * junta, con `repeatCount`), con su tema: la lista "Lo que trabajamos hoy".
   * El headline cuenta las no descartadas. Los focos se ligan a estos ids
   * (`correctionIds`).
   */
  distinctCorrections: ReadonlyArray<SessionSummaryDistinctCorrection>;
  /**
   * Correcciones fonéticas persistidas. Opcional para tolerar respuestas del
   * backend previas al fix (cuando el campo no existía) — el debrief cae a `[]`.
   */
  phoneticCorrections?: ReadonlyArray<SessionSummaryPhoneticCorrection>;
  cefrEstimate?: CefrLevel;
  /**
   * Real move of the official level this session triggered (`level_change`).
   * `from` can be 'A0': the A0 → A1 graduation by evidence (plan 48 phase 3).
   */
  cefrDelta?: { from: CefrLevel | 'A0'; to: CefrLevel; direction: 'up' | 'down' | 'same' };
  /**
   * Delta de cobertura de Biblioteca (fase 20.G). Ausente en respuestas viejas
   * del backend o cuando no se usó ninguna palabra del catálogo.
   */
  coverageDelta?: SessionSummaryCoverageDelta;
}

export interface SessionSummaryProgram {
  unit: { id: string; title: string; cefr: CefrLevel; dimension: EnglishDimension };
  /** La unit ESTÁ dominada (estado). Pinta el sello "Dominada". */
  mastered: boolean;
  /**
   * ESTA sesión la dominó (evento del registro por sesión). Es lo ÚNICO que
   * dispara el festejo de unidad: un repaso de una unit dominada trae
   * `mastered` pero no `masteredNow`.
   */
  masteredNow: boolean;
  /**
   * The session's per-session record exists. `false` → `masteredNow=false` is
   * "unknown" (the record's merge failed), so the debrief does not call the
   * session a review. Absent in older backend responses.
   */
  sessionRecorded?: boolean;
  /** Dominio de la unit actual, 0..1. Ausente en respuestas viejas → 0. */
  mastery?: number;
  /**
   * Próxima SUB-unidad cuando la unit quedó a medias. Excluyente con
   * `nextUnit`: mientras falten sub-unidades el próximo paso del plan es seguir
   * en la MISMA unit. Ausente en units legacy y en respuestas viejas.
   */
  nextSubUnit?: { id: string; unitId: string; title: string; order: number; total: number };
  /** `hasTheory`: la unit es autorada (subUnits con teoría) → el debrief ofrece
   * "Leé la teoría". Opcional para tolerar respuestas viejas del backend. */
  nextUnit?: { id: string; title: string; cefr: CefrLevel; hasTheory?: boolean };
}

/** Objetivo del Programa en el que la sesión sumó reps (espejo del backend). */
export interface SessionSummaryProgramObjective {
  index: number;
  /** El "can …" del currículum (inglés), el mismo que mostró el tracker. */
  criterion: string;
  /** Conteo al cerrar (lo que mostró el anillo) y su meta. */
  count: number;
  reps: number;
  /** Reps ganadas en ESTA sesión. */
  gained: number;
  /** Palabras literales del alumno que valieron la última rep. */
  evidence?: string;
  /**
   * The catalog skill that absorbs this objective (W6): the debrief names the
   * row by its Spanish label and merges it with that skill's credits. Absent
   * when no skill absorbs it (or in older backend responses).
   */
  skill?: { id: string; label: { es: string; en: string } };
}

export interface SessionSummaryProgramSubUnitCredits {
  id: string;
  order: number;
  total: number;
  titleEs: string;
  /** Ya estaba completa: la sesión la repasó. */
  review: boolean;
  /** ESTA sesión la completó (nunca en un repaso). */
  completedNow: boolean;
  objectives: ReadonlyArray<SessionSummaryProgramObjective>;
}

/**
 * Lo que la sesión ganó en el Programa, por unit (D8 "Lo que lograste hoy").
 * Final desde el cierre (no depende del distill).
 */
export interface SessionSummaryProgramCredits {
  unit: { id: string; title: string; titleEs?: string };
  masteredNow: boolean;
  subUnits: ReadonlyArray<SessionSummaryProgramSubUnitCredits>;
}

/**
 * A catalog skill the session credited (W6, D7 / D9) — mirror of
 * `SessionSummarySkillCredit` in module-languages. Built from the DB
 * (survives a reload): the live credits are final from the close, the
 * session-end judge's (`sources: ['session']`, `turnIndex: null`) land with
 * the distill — late data. Praise a correction of the same turn contradicts
 * is already left out by the backend.
 */
export interface SessionSummarySkillCredit {
  skillId: string;
  label: { es: string; en: string };
  area: 'grammar' | 'words' | 'conversation';
  level: CefrLevel;
  /** The catalog celebrates it; `false` = a silent credit (listed only when it nailed a focus). */
  celebrate: boolean;
  /** The learner's words that earned it. */
  evidence: string;
  turnIndex: number | null;
  /** `capstone`: a watched skill of a can-do capstone (plan 46), a bonus credit. */
  sources: ReadonlyArray<'judge' | 'lexicon' | 'lesson' | 'session' | 'capstone'>;
  /** Reps this session moved (0..2). */
  gained: number;
  /** Counted reps once the session ended (may exceed `repsToComplete`). */
  reps: number;
  repsToComplete: number;
  /** This session completed the skill. */
  completedNow: boolean;
  completedBefore: boolean;
  /** A radar focus this session nailed with it (D9), or null. */
  nailedFocus: { focusId: string; label: string | null } | null;
}

/**
 * Tira de esfuerzo del debrief (espejo de `SessionSummaryEffort` del backend).
 * Datos crudos siempre presentes — el piso garantizado del cierre.
 */
export interface SessionSummaryEffort {
  /**
   * Tiempo de charla en SEGUNDOS (tiempo activo de la sesión). `null` mientras
   * aún no se puede calcular (el backend todavía no persistió `ended_at`): el
   * debrief muestra la medalla en "cargando" hasta tener el valor real,
   * evitando el salto "1m → real".
   *
   * En segundos y no en minutos porque el redondeo es del front — y con él la
   * decisión entre "0" y "<1", que el backend no puede tomar sin perder la
   * diferencia entre "no habló" y "habló poco". Ver `lib/chatMinutes.ts`.
   */
  activeSeconds: number | null;
  /**
   * De ese tiempo, cuántos segundos habló el alumno en voz alta ("de eso,
   * hablaste X min"). Mismo gate de `null`. Opcional para tolerar respuestas
   * viejas del backend: sin él, el debrief no muestra la sub-línea.
   */
  spokenSeconds?: number | null;
  turns: number;
  words: number;
  streak: number;
}

/**
 * Medidor cualitativo de fluidez (espejo de `SessionSummaryFluidity`). Ausente
 * cuando no hay señal para estimarlo → el debrief muestra el estado "sin datos".
 */
export interface SessionSummaryFluidity {
  level: number;
  max: number;
  caption: string;
  title: string;
  note: string;
}

/**
 * Resultado de un objetivo de misión evaluado por el juez del debrief
 * (espejo de `SessionSummaryObjectiveResult` de `@orborix/module-languages`).
 * `evidence` (frase literal del user) sólo está presente cuando `met=true`.
 * Vacío en escenarios generados (roleplay inmersivo sin misión).
 */
export interface SessionSummaryObjectiveResult {
  id: string;
  label: string;
  kind: 'primary' | 'bonus';
  met: boolean;
  evidence?: string;
  /** Charla asistida: no se cumplió solo, pero se practicó con la respuesta modelo. */
  practicedWithHelp: boolean;
}

export interface SessionSummaryScenario {
  scenarioId: string;
  scenarioTitle: string;
  /** La escena se hizo (al menos en parte) con la respuesta modelo. */
  assisted: boolean;
  /** Veredicto por objetivo con evidencia. Vacío en generados. */
  objectives: ReadonlyArray<SessionSummaryObjectiveResult>;
  /**
   * `true` mientras el juez de objetivos (job separado, más lento) todavía no
   * escribió. El cliente sigue poleando y la misión se muestra "evaluando…".
   * Ausente en respuestas viejas del backend → el debrief lo trata como false.
   */
  objectivesPending?: boolean;
  phrasesUsedWell: ReadonlyArray<string>;
  coachSuggestions: ReadonlyArray<string>;
}

export interface SessionSummaryFree {
  topics: ReadonlyArray<string>;
}

/**
 * The can-do section of the debrief (plan 46) — mirror of
 * `SessionSummaryCanDo` in module-languages. A capstone's pass reads the
 * scenario judge's verdicts, so while `scenario.objectivesPending` it can
 * still move (the summary keeps polling).
 */
export interface SessionSummaryCanDo {
  unitId: string;
  canDo: LocalizedText;
  step:
    | { kind: 'lesson'; lessonId: string; title: LocalizedText }
    | { kind: 'capstone'; title: LocalizedText };
  unitStatus: CanDoUnitStatus;
  lessonsDone: number;
  lessonsTotal: number;
  capstone: { status: CanDoCapstoneStatus; passedNow: boolean };
  /** This session completed the unit (lessons + capstone): the big celebration. */
  unitCompletedNow: boolean;
  next: CanDoNextStepView;
}

/**
 * Recomendación ÚNICA del cierre (plan 26 F4) — espejo de
 * `SessionSummaryNextStep` del backend. Un solo motor server-side decide
 * (foco ligado a una corrección de hoy → errores de la sesión → foco
 * prioritario → conversación);
 * reemplaza a DebriefReco + free.suggestedUnit + el CTA suelto del footer.
 */
export interface SessionSummaryNextStep {
  kind: 'unit' | 'practice' | 'conversation';
  /** Por qué esta recomendación (es-AR). */
  reason: string;
  /** Sólo en kind='unit'. The app shows `titleEs` when there is one (`unitTitle`). */
  unit?: {
    id: string;
    title: string;
    titleEs?: string;
    cefr: CefrLevel;
    hasTheory: boolean;
    /** The lesson the recommendation opens (the CTA starts there, not where the unit was left). */
    subUnitId?: string;
    /**
     * The skill that routed the focus to this unit: the CTA names it
     * ("Practicá contables e incontables") — the unit can be about something
     * else ("El trabajo: oficios y tareas" holds the work-vs-job lesson).
     */
    skill?: { id: string; label: { es: string; en: string } };
  };
}

/**
 * Foco de repaso para el bloque "Tu progreso" (plan 26): contadores REALES de
 * `english_review_focus`. Espejo de `ReviewFocusSummaryItem` del backend.
 */
export interface ReviewFocusSummaryItem {
  id: string;
  pattern: string;
  /** Label corto del foco (≤60, 2ª persona, es-AR). Ausente → se muestra `pattern`. */
  label?: string;
  category?: string;
  dimension?: string;
  /** # de sesiones distintas con el error (el contador honesto). */
  hitSessions: number;
  /** Sesiones limpias consecutivas (uso correcto observado). */
  cleanStreak: number;
  /** Umbral de retiro del nivel (2 en A/B, 3 en C). */
  streakNeed: number;
  status: 'active' | 'resolved';
  /** Señal de ESTA sesión: bien / reapareció / esquivado / sin exposición. */
  outcome: 'correct_use' | 'error' | 'avoided' | 'none';
  example?: { said: string; fix: string };
  /** Solo en resolved — el festejo grande es solo para 'system'. */
  resolvedBy?: 'system' | 'user';
  /** El foco nació en esta sesión ("Empezamos a mirar: …"). */
  isNew: boolean;
  /**
   * Ids de las filas de `common.distinctCorrections` que este foco agrupa hoy
   * (radar anclado). `[]` = sin corrección de hoy (festejo, uso correcto,
   * esquivado). Ausente en respuestas viejas del backend.
   */
  correctionIds?: ReadonlyArray<string>;
}

export interface SessionSummary {
  sessionId: string;
  mode: 'plan' | 'free' | 'calibration';
  intensity: string;
  unitId: string | null;
  scenarioId: string | null;
  minimal: boolean;
  headline: SessionSummaryHeadline;
  /** Tira de esfuerzo — siempre presente. Ausente en respuestas viejas. */
  effort?: SessionSummaryEffort;
  common: SessionSummaryCommon;
  /**
   * Focos de repaso para "Tu progreso" (plan 26). `[]` = sin focos (estado
   * vacío); ausente en respuestas viejas del backend.
   */
  reviewFoci?: ReadonlyArray<ReviewFocusSummaryItem>;
  /** Medidor de fluidez. Ausente cuando no hay señal. */
  fluidity?: SessionSummaryFluidity;
  /** Recomendación única del cierre (F4). Ausente en respuestas viejas. */
  nextStep?: SessionSummaryNextStep;
  /**
   * `true` mientras el análisis post-sesión (distill) no terminó, dentro de su
   * gracia: lo que llega tarde (el resultado del radar en `reviewFoci`) todavía
   * puede cambiar. El hook sigue poleando y el debrief no lo muestra como
   * definitivo. Ausente en respuestas viejas del backend.
   */
  analysisPending?: boolean;
  /** Lo que la sesión ganó en el Programa (D8). Ausente si no ganó nada. */
  programCredits?: ReadonlyArray<SessionSummaryProgramCredits>;
  /** Skills of the catalog the session credited (W6). Absent if none. */
  skillCredits?: ReadonlyArray<SessionSummarySkillCredit>;
  program?: SessionSummaryProgram;
  /**
   * A can-do session (plan 46): its unit, the step it ran, the unit's state
   * now, the capstone pass / unit completion of THIS session, and the path's
   * next step. Absent outside the path.
   */
  canDo?: SessionSummaryCanDo;
  scenario?: SessionSummaryScenario;
  free?: SessionSummaryFree;
  /**
   * Presente sólo si la sesión se jugó bajo overlay de exploración (plan 38 F3,
   * §3.7): nota sutil "jugaste explorando {playedCefr}" + aviso de que NO afecta
   * el nivel real ni el progreso. Ausente en sesiones normales y respuestas
   * viejas del backend.
   */
  exploring?: { playedCefr: CefrLevel };
}

/**
 * Parcial del debrief: secciones ya FINALES al cierre de la sesión, antes de
 * que el distill termine — correcciones (persistidas en vivo durante la
 * charla), fonéticas y fluidez. El debrief las revela de entrada; el resto de
 * las secciones espera al summary completo con su skeleton.
 */
export interface SessionSummaryPartial {
  sessionCorrections: ReadonlyArray<SessionSummaryCorrection>;
  /** Ver `SessionSummaryCommon.distinctCorrections` (temas sin focos todavía). */
  distinctCorrections: ReadonlyArray<SessionSummaryDistinctCorrection>;
  fluidity?: SessionSummaryFluidity;
  /** The session's skill credits so far (a session still running after an F5 included). Absent if none. */
  skillCredits?: ReadonlyArray<SessionSummarySkillCredit>;
}

export interface SessionSummaryResponse {
  ready: boolean;
  summary: SessionSummary | null;
  /**
   * Tira de esfuerzo precomputada. Presente incluso con `ready:false` para que
   * el debrief la muestre de entrada mientras el distill carga (render
   * progresivo). Cuando `ready:true`, `summary.effort` trae el mismo dato.
   */
  effort?: SessionSummaryEffort;
  /**
   * Secciones ya finales, presentes con `ready:false` (render progresivo por
   * sección). Cuando `ready:true`, `summary.common` trae los mismos datos.
   */
  partial?: SessionSummaryPartial;
}

const client = createTypedApiClient({
  basePath: BASE,
  errorName: 'SessionSummaryApiError',
  // El polling del debrief cancela al desmontar: el caller distingue "aborté
  // yo" de "se cayó la red" para no pintar el error.
  abortError: 'rethrow',
});

export const SessionSummaryApiError = client.ApiError;
export type SessionSummaryApiError = InstanceType<typeof client.ApiError>;

/**
 * Pega contra `/sessions/:sessionId/summary`. Acepta `AbortSignal` para que
 * el caller pueda cancelar en mid-flight (importante para el polling: cuando
 * el componente desmonta, queremos abortar la request actual sin que pegue
 * setState después del unmount).
 */
export async function fetchSessionSummary(
  sessionId: string,
  signal?: AbortSignal,
  opts?: {
    /**
     * El caller ya tiene el `partial` (es estático post-cierre): pedimos al
     * backend que no lo recompute en cada tick del polling (`?partial=0`).
     */
    skipPartial?: boolean;
  },
): Promise<SessionSummaryResponse> {
  // `tz` es obligatoria: la racha de la tira de esfuerzo se agrupa por día
  // LOCAL (misma fuente que el hero de la Home, `/progress/totals`). Sin ella
  // el backend responde 400 en vez de asumir UTC.
  const params = new URLSearchParams({ tz: browserTimeZone() });
  if (opts?.skipPartial) params.set('partial', '0');
  const qs = `?${params.toString()}`;
  // El backend envuelve en `{ success, data }`; el desenvuelto del cliente
  // acepta también un shape plano (defensa en profundidad).
  return client.get<SessionSummaryResponse>(
    `/sessions/${encodeURIComponent(sessionId)}/summary${qs}`,
    signal ? { signal } : {},
  );
}
